import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, RefreshCw, Plus, Trash2 } from "lucide-react";

// ── UUID v4 generator ────────────────────────────────────────

function uuidv4(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// Simple ULID generator (monotonic)
function ulid(): string {
    const ENCODING = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
    const ts = Date.now();
    let timeStr = "";
    let t = ts;
    for (let i = 0; i < 10; i++) {
        timeStr = ENCODING[t % 32]! + timeStr;
        t = Math.floor(t / 32);
    }
    let random = "";
    for (let i = 0; i < 16; i++) {
        random += ENCODING[Math.floor(Math.random() * 32)]!;
    }
    return timeStr + random;
}

type IdType = "uuid-v4" | "ulid";

// ── Component ────────────────────────────────────────────────

export function UuidGenerator() {
    const [ids, setIds] = useState<string[]>([]);
    const [idType, setIdType] = useState<IdType>("uuid-v4");
    const [count, setCount] = useState(1);
    const [copied, setCopied] = useState<number | "all" | null>(null);

    useEffect(() => {
        document.title =
            "UUID & ULID Generator — Free Online ID Generator — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Generate UUID v4 and ULID identifiers instantly. Bulk generation, copy to clipboard. Free tool, runs in your browser.",
            );
        }
    }, []);

    const generate = useCallback(() => {
        const fn = idType === "uuid-v4" ? uuidv4 : ulid;
        setIds(Array.from({ length: count }, fn));
    }, [idType, count]);

    // Generate on load and when params change
    useEffect(() => {
        generate();
    }, [generate]);

    const copyOne = async (id: string, idx: number) => {
        await navigator.clipboard.writeText(id);
        setCopied(idx);
        setTimeout(() => setCopied(null), 2000);
    };

    const copyAll = async () => {
        await navigator.clipboard.writeText(ids.join("\n"));
        setCopied("all");
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            {/* Nav */}
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">
                    ballah code
                </Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">UUID Generator</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">UUID & ULID Generator</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Generate random UUID v4 or ULID identifiers. Bulk generate up to 100 at a time.
                    </p>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-3 mb-6 flex-wrap">
                    <div className="flex rounded-lg border border-white/[0.06] overflow-hidden">
                        {([{ key: "uuid-v4", label: "UUID v4" }, { key: "ulid", label: "ULID" }] as const).map(({ key, label }) => (
                            <button
                                key={key}
                                onClick={() => setIdType(key)}
                                className={`px-3.5 py-2 text-[12px] transition-colors ${idType === key ? "bg-white/[0.08] text-zinc-200" : "text-zinc-600 hover:text-zinc-400"}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-1.5 text-[12px] text-zinc-600">
                        <span>Count:</span>
                        {[1, 5, 10, 25, 100].map((n) => (
                            <button
                                key={n}
                                onClick={() => setCount(n)}
                                className={`px-2 py-1 rounded text-[12px] transition-colors ${count === n ? "bg-white/[0.08] text-zinc-300" : "text-zinc-600 hover:text-zinc-400"}`}
                            >
                                {n}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={generate}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-violet-400/20 bg-violet-400/10 text-[12px] text-violet-400 hover:bg-violet-400/15 transition-colors ml-auto"
                    >
                        <RefreshCw size={12} />
                        Regenerate
                    </button>

                    {ids.length > 1 && (
                        <button
                            onClick={copyAll}
                            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors"
                        >
                            {copied === "all" ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                            {copied === "all" ? "Copied all" : "Copy all"}
                        </button>
                    )}
                </div>

                {/* IDs */}
                <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden mb-8">
                    {ids.map((id, i) => (
                        <div
                            key={i}
                            className={`flex items-center justify-between px-4 py-3 group ${i < ids.length - 1 ? "border-b border-white/[0.04]" : ""}`}
                        >
                            <code className="text-[13px] text-zinc-300 font-mono">{id}</code>
                            <button
                                onClick={() => copyOne(id, i)}
                                className="text-zinc-700 hover:text-zinc-300 transition-colors opacity-0 group-hover:opacity-100"
                            >
                                {copied === i ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
                            </button>
                        </div>
                    ))}
                </div>

                {/* Add more */}
                <button
                    onClick={() => {
                        const fn = idType === "uuid-v4" ? uuidv4 : ulid;
                        setIds((prev) => [...prev, ...Array.from({ length: count }, fn)]);
                    }}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-600 hover:text-zinc-300 transition-colors mb-16"
                >
                    <Plus size={12} />
                    Add {count} more
                </button>

                {/* SEO */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">What is a UUID?</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>A UUID (Universally Unique Identifier) is a 128-bit identifier that is unique across space and time. UUID v4 is generated using random numbers, making collisions extremely unlikely. ULIDs (Universally Unique Lexicographically Sortable Identifiers) are similar but encode a timestamp, making them sortable by creation time.</p>
                        <p>UUIDs are used as primary keys in databases, session identifiers, API keys, and anywhere you need a guaranteed-unique identifier without coordination between systems.</p>
                        <p>Everything runs entirely in your browser. Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/hash-generator" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Hash Generator</Link>
                        <Link to="/tools/unix-timestamp" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Unix Timestamp Converter</Link>
                        <Link to="/tools/base64" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Base64 Encode / Decode</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
